import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  AccountBalanceWallet as WalletIcon,
  Business as BranchIcon,
  CreditCard as CardIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  Store as SalesPointIcon,
  TrendingDown as DebitIcon,
  TrendingUp as CreditIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import HolderAvatar from "../../components/layout/HolderAvatar";
import { transactionAPI } from "../../services/transactionAPI";
import { tokens } from "../../theme";

const formatCurrency = (amount: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const formatWhen = (value?: string) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm:ss");
  } catch {
    return value;
  }
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25, wordBreak: "break-word" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const sectionCardSx = {
  borderRadius: tokens.radius.md,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
  bgcolor: "background.paper",
} as const;

const TransactionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: tx, isLoading, error } = useQuery({
    queryKey: ["transaction", id],
    queryFn: () => transactionAPI.getTransactionById(id!),
    enabled: Boolean(id),
    staleTime: 0,
    refetchOnMount: "always",
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tx) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/transactions")}
          sx={{ mb: 2 }}
        >
          Back to transactions
        </Button>
        <Alert severity="error">
          {(error as Error)?.message || "Transaction not found"}
        </Alert>
      </Box>
    );
  }

  const isCredit = tx.type === "credit";
  const accent = isCredit ? tokens.colors.success.main : tokens.colors.error.main;
  const accentBg = alpha(accent, 0.12);
  const holder = tx.cardHolder;
  const creator = tx.creator;

  return (
    <Box>
      <PageHeader
        title="Transaction details"
        subtitle={formatWhen(tx.transactionDate)}
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Transactions", to: "/transactions" },
          { label: isCredit ? "Credit" : "Debit" },
        ]}
        action={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/transactions")}
          >
            Back
          </Button>
        }
      />

      {/* Amount summary */}
      <Paper
        elevation={0}
        sx={{
          ...sectionCardSx,
          mb: 2.5,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.75)} 100%)`,
            color: "#fff",
            px: { xs: 2.5, md: 3 },
            py: 2.5,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: tokens.radius.md,
                  bgcolor: alpha("#fff", 0.2),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isCredit ? (
                  <CreditIcon sx={{ fontSize: 32 }} />
                ) : (
                  <DebitIcon sx={{ fontSize: 32 }} />
                )}
              </Box>
              <Box>
                <Chip
                  label={isCredit ? "Credit" : "Debit"}
                  size="small"
                  sx={{
                    bgcolor: alpha("#fff", 0.25),
                    color: "#fff",
                    fontWeight: 700,
                    mb: 0.75,
                  }}
                />
                <Typography variant="h4" fontWeight={800} lineHeight={1.1}>
                  {isCredit ? "+" : "−"}
                  {formatCurrency(Math.abs(Number(tx.amount)))}
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={0.5} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <ScheduleIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.95 }}>
                  {formatWhen(tx.transactionDate)}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ opacity: 0.8, fontFamily: "monospace" }}>
                ID: {tx.id.slice(0, 8)}…
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <DetailRow label="Balance before" value={formatCurrency(tx.balanceBefore)} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <DetailRow label="Balance after" value={formatCurrency(tx.balanceAfter)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailRow
                label="Change"
                value={
                  <Typography
                    component="span"
                    fontWeight={700}
                    color={isCredit ? "success.main" : "error.main"}
                  >
                    {isCredit ? "+" : "−"}
                    {formatCurrency(Math.abs(Number(tx.amount)))}
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Paper>

      <Grid container spacing={2.5}>
        {/* Performed by */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ ...sectionCardSx, height: "100%" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <PersonIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Performed by
                </Typography>
              </Stack>
              {creator ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <HolderAvatar name={creator.name} size={52} />
                  <Box minWidth={0}>
                    <Typography variant="body1" fontWeight={700}>
                      {creator.name}
                    </Typography>
                    {creator.email && (
                      <Typography variant="body2" color="text.secondary">
                        {creator.email}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      sx={{ mt: 1, px: 0, minWidth: 0 }}
                      onClick={() => navigate(`/users/${creator.id}`)}
                    >
                      View user profile
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  System / unknown user
                  {tx.createdBy && (
                    <Typography
                      component="span"
                      display="block"
                      variant="caption"
                      fontFamily="monospace"
                      mt={0.5}
                    >
                      User ID: {tx.createdBy}
                    </Typography>
                  )}
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5}>
                <DetailRow label="Recorded at" value={formatWhen(tx.createdAt)} />
                <DetailRow label="Transaction time" value={formatWhen(tx.transactionDate)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Card holder */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ ...sectionCardSx, height: "100%" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <CardIcon color="secondary" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Card holder
                </Typography>
              </Stack>
              {holder ? (
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <HolderAvatar name={holder.holderName} size={52} />
                  <Box minWidth={0} flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body1" fontWeight={700}>
                        {holder.holderName}
                      </Typography>
                      {holder.isActive !== undefined && (
                        <Chip
                          label={holder.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={holder.isActive ? "success" : "default"}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    {holder.phone && (
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {holder.phone}
                      </Typography>
                    )}
                    {holder.email && (
                      <Typography variant="body2" color="text.secondary">
                        {holder.email}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ mt: 1.5 }}
                      onClick={() => navigate(`/cardholders/${holder.id}`)}
                    >
                      View holder profile
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Holder details unavailable
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Location & links */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={sectionCardSx}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <BranchIcon color="action" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Branch & salesPoint
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DetailRow label="Branch" value={tx.branch?.name || "—"} />
                  {tx.branch?.address && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {tx.branch.address}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <SalesPointIcon color="action" sx={{ mt: 0.3, fontSize: 20 }} />
                    <DetailRow label="Sales point" value={tx.salesPoint?.name || "—"} />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Related records */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={sectionCardSx}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <ReceiptIcon color="action" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Related records
                </Typography>
              </Stack>
              <Stack spacing={1.5}>
                <DetailRow
                  label="Linked sale"
                  value={
                    tx.saleId ? (
                      <Typography variant="caption" fontFamily="monospace">
                        {tx.saleId}
                      </Typography>
                    ) : (
                      "—"
                    )
                  }
                />
                <DetailRow
                  label="Card holder ID"
                  value={
                    <Typography variant="caption" fontFamily="monospace">
                      {tx.cardHolderId}
                    </Typography>
                  }
                />
                <DetailRow
                  label="Full transaction ID"
                  value={
                    <Typography variant="caption" fontFamily="monospace">
                      {tx.id}
                    </Typography>
                  }
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Balance flow */}
        <Grid item xs={12}>
          <Card elevation={0} sx={sectionCardSx}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <WalletIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Balance movement
                </Typography>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="center"
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    flex: 1,
                    textAlign: "center",
                    borderRadius: tokens.radius.sm,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Before
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {formatCurrency(tx.balanceBefore)}
                  </Typography>
                </Paper>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  color={isCredit ? "success.main" : "error.main"}
                  sx={{ px: 1 }}
                >
                  {isCredit ? "→ +" : "→ −"}
                  {formatCurrency(Math.abs(Number(tx.amount)))}
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    flex: 1,
                    textAlign: "center",
                    borderRadius: tokens.radius.sm,
                    borderColor: accent,
                    bgcolor: accentBg,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    After
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {formatCurrency(tx.balanceAfter)}
                  </Typography>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransactionDetailsPage;
